import type { AdminBooking, AdminBookingStatus } from '@/api/core/bookings';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { PropertyCell } from '@/components/copy-id-button';
import {
  clearListParams,
  ListFilterToolbar,
  patchListParams,
} from '@/components/list-filter-toolbar';
import { ListNote, TABLE_BODY_CLASS } from '@/components/list-note';
import { usePropertyDirectory } from '@/components/property-filter';
import { RowLink } from '@/components/row-link';
import { formatDateTimeWithOffset } from '@/lib/format';
import {
  Badge,
  Button,
  DataTable,
  ListPage,
  PageHeader,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router';
import {
  bookingListQuery,
  bookingRuntime,
  hasDomainAccess,
  type BookingRuntime,
} from './booking-queries';
import { bookingDetailHref, bookingListState } from './booking-url';

const statusLabels: Record<AdminBookingStatus, string> = {
  PENDING_PAYMENT: '결제 대기',
  CONFIRMED: '예약 확정',
  CANCELLED: '예약 취소됨',
};
const statusTones: Record<AdminBookingStatus, 'warning' | 'success' | 'danger'> = {
  PENDING_PAYMENT: 'warning',
  CONFIRMED: 'success',
  CANCELLED: 'danger',
};
const statusOptions = [
  { value: 'PENDING_PAYMENT', label: '결제 대기' },
  { value: 'CONFIRMED', label: '예약 확정' },
  { value: 'CANCELLED', label: '예약 취소됨' },
];

const FILTER_KEYS = {
  propertyId: 'property_id',
  status: 'status',
  periodStart: 'starts_from',
  periodEnd: 'starts_before',
};

function OrderIds({ ids }: { ids: readonly string[] }) {
  if (!ids.length) return <span className="text-muted-foreground">연결 ID 없음</span>;
  const [first, ...rest] = ids;
  return (
    <span className="flex flex-wrap items-center gap-1.5 whitespace-normal">
      <span className="break-all">{first}</span>
      {rest.length > 0 && (
        <Badge tone="neutral" title={rest.join(', ')}>
          +{rest.length}
        </Badge>
      )}
    </span>
  );
}

function BookingLoadFailure({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-2 p-4" role="alert">
      <p className="body-sm">예약 현황을 불러오지 못했습니다. 연결 상태를 확인해 주세요.</p>
      <Button variant="outline" onClick={onRetry}>
        다시 조회
      </Button>
    </div>
  );
}

export function BookingListPage({
  runtime = bookingRuntime,
  enableServerSorting = false,
}: {
  runtime?: BookingRuntime;
  enableServerSorting?: boolean;
}) {
  const session = useOperatorSession(runtime.session);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const state = bookingListState(params);
  const invalid =
    !state.valid ||
    (!enableServerSorting && (state.sortBy !== null || state.sortDirection !== null));
  const sortModeAllowed =
    enableServerSorting || (state.sortBy === null && state.sortDirection === null);
  const allowed = hasDomainAccess(runtime.session, session.generation, 'booking', 2);
  const list = useQuery(
    bookingListQuery(
      runtime,
      session.generation,
      {
        cursor: state.cursor,
        propertyId: state.propertyId ?? undefined,
        status: state.status ?? undefined,
        startsFrom: state.startsFrom ?? undefined,
        startsBefore: state.startsBefore ?? undefined,
        ...(enableServerSorting && state.sortBy && state.sortDirection
          ? { sortBy: state.sortBy, sortDirection: state.sortDirection }
          : {}),
      },
      !invalid && allowed,
    ),
  );
  const activeSort =
    enableServerSorting && state.sortBy && state.sortDirection
      ? `${state.sortBy},${state.sortDirection}`
      : null;
  const directory = usePropertyDirectory(runtime);
  const hasFilters = [state.propertyId, state.status, state.startsFrom, state.startsBefore].some(
    Boolean,
  );
  const columns: DataTableColumn<AdminBooking>[] = [
    {
      key: 'booking_id',
      sortKey: 'booking_id',
      sortable: enableServerSorting,
      header: '예약 번호',
      cell: (row) => (
        <RowLink to={bookingDetailHref(row.booking_id, location.pathname + location.search)}>
          {row.booking_id}
        </RowLink>
      ),
    },
    {
      key: 'status',
      sortKey: 'status',
      sortable: enableServerSorting,
      header: '예약 상태',
      cell: (row) => <Badge tone={statusTones[row.status]}>{statusLabels[row.status]}</Badge>,
    },
    {
      key: 'property_id',
      sortable: false,
      header: '지점',
      cell: (row) => (
        <PropertyCell id={row.property_id} name={directory.names?.get(row.property_id)} />
      ),
    },
    {
      key: 'start_at',
      sortKey: 'start_at',
      sortable: enableServerSorting,
      header: '이용 시작',
      cell: (row) => <span className="tabular-nums">{formatDateTimeWithOffset(row.start_at)}</span>,
    },
    {
      key: 'end_at',
      sortKey: 'end_at',
      sortable: enableServerSorting,
      header: '이용 종료',
      cell: (row) => <span className="tabular-nums">{formatDateTimeWithOffset(row.end_at)}</span>,
    },
    {
      key: 'order_ids',
      sortable: false,
      header: '연결 주문 번호',
      cell: (row) => <OrderIds ids={row.order_ids} />,
    },
  ];
  if (session.status !== 'authorized') return null;
  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title="개인 예약 현황"
          description="지점별 개인 예약의 저장 상태와 연결된 주문 번호를 확인합니다."
          className="items-start sm:items-center"
        />
      </ShellHeader>
      {allowed && state.valid && sortModeAllowed ? (
        <>
          <ListFilterToolbar
            directory={directory}
            propertyId={state.propertyId}
            status={state.status}
            statusLabel="예약 상태"
            statusOptions={statusOptions}
            periodStart={state.startsFrom}
            periodEnd={state.startsBefore}
            periodPlaceholder="예약 시작 기간"
            isFetching={list.isFetching && !list.isPending}
            onChange={(patch) => {
              setParams(patchListParams(params, patch, FILTER_KEYS));
            }}
            onReset={() => {
              setParams(clearListParams(params, FILTER_KEYS));
            }}
          />
          <ListNote>
            예약 시작 일시가 고른 날짜 범위(현재 기기 시간대)에 드는 예약을 한 번에 최대 20건씩 보여
            줍니다. 조회 전용 화면이라 서명·취소·환불 같은 작업은 제공하지 않습니다.
          </ListNote>
          <ListPage.Body className="border-t">
            {list.isError ? (
              <BookingLoadFailure onRetry={() => void list.refetch()} />
            ) : (
              <div className={TABLE_BODY_CLASS}>
                <DataTable
                  columns={columns}
                  data={
                    list.isPending && list.fetchStatus === 'fetching'
                      ? []
                      : (list.data?.items ?? [])
                  }
                  sort={activeSort}
                  onSortChange={
                    enableServerSorting
                      ? (sort) => {
                          const next = new URLSearchParams(params);
                          next.delete('cursor');
                          if (sort) {
                            const [sortBy, sortDirection] = sort.split(',');
                            next.set('sort_by', sortBy ?? '');
                            next.set('sort_direction', sortDirection ?? '');
                          } else {
                            next.delete('sort_by');
                            next.delete('sort_direction');
                          }
                          setParams(next);
                        }
                      : undefined
                  }
                  rowKey={(row) => row.booking_id}
                  isLoading={list.isPending && list.fetchStatus === 'fetching'}
                  empty={
                    hasFilters
                      ? {
                          title: '조건에 맞는 개인 예약이 없습니다',
                          description: state.status
                            ? `예약 상태가 ‘${statusLabels[state.status]}’인 예약을 찾지 못했습니다. 조건을 바꾸거나 초기화해 보세요.`
                            : '지점·상태·시작 일시 조건을 바꾸거나 조건을 초기화해 보세요.',
                        }
                      : {
                          title: '표시할 개인 예약이 없습니다',
                          description:
                            '다른 지점이나 시작 일시 범위를 골라 다시 조회해 볼 수 있습니다.',
                        }
                  }
                />
              </div>
            )}
          </ListPage.Body>
          <ListPage.Pagination>
            <div className="flex flex-wrap items-center gap-3">
              {state.cursor && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.delete('cursor');
                    setParams(next);
                  }}
                >
                  처음으로
                </Button>
              )}
              {list.status === 'success' && list.data.nextCursor !== null && (
                <Button
                  disabled={list.isFetching}
                  onClick={() => {
                    const nextCursor = list.data.nextCursor;
                    if (nextCursor === null) return;
                    const next = new URLSearchParams(params);
                    next.set('cursor', nextCursor);
                    setParams(next);
                  }}
                >
                  다음 20건
                </Button>
              )}
            </div>
          </ListPage.Pagination>
        </>
      ) : allowed ? (
        <div className="flex flex-col items-start gap-3 p-6" role="alert">
          <p>필터 주소가 올바르지 않습니다. 시작 시각은 시간대가 포함된 범위여야 합니다.</p>
          <Button
            nativeButton={false}
            role="link"
            variant="outline"
            render={<Link to="/contracts" />}
          >
            필터 초기화
          </Button>
        </div>
      ) : (
        <div className="p-6" role="alert">
          현재 예약 조회 권한이 없습니다. 관리자에게 권한을 확인해 주세요.
        </div>
      )}
    </ListPage>
  );
}
